import { useCallback, useRef, useState, type Dispatch, type SetStateAction } from 'react';

/**
 * Spec 380 §4.1: generischer localStorage-State-Hook — kodifiziert das mehrfach
 * kopierte Idiom (`useServicesViewMode`, `useSnippetContext`): Lazy-Init aus dem
 * Storage, best-effort try/catch um Read+Write (Privacy-Mode/Quota-sicher), optionaler
 * Form-Guard und harter Default.
 *
 * Bewusst „dumm" und typ-agnostisch: `JSON.parse` liefert zurück, was es liefert — ein
 * als String abgelegtes Datum kommt als String zurück, nicht als `Date`. Reiche Typen
 * marshallt der Consumer selbst (siehe Subscriptions + `lib/utils/date-param`).
 *
 * `validate` prüft NUR die Form/den Typ (richtiger Typ, erwartete Felder) — schlägt es
 * fehl oder ist der gespeicherte Wert kein gültiges JSON, greift `defaultValue`.
 * Domänen-Validierung (existiert diese ID noch?) gehört NICHT hierher: sie braucht
 * asynchron geladene Daten und passiert im konsumierenden Component zur Render-Zeit.
 *
 * Der Setter spiegelt die `useState`-Signatur (direkter Wert ODER Updater `prev => next`).
 * Ein neuer Wert von `undefined` ruft `removeItem` statt `setItem` — denn
 * `JSON.stringify(undefined)` liefert keine gültige JSON-Zeichenkette und würde den
 * String `"undefined"` ablegen, an dem `JSON.parse` später wirft.
 */
export function useLocalStorage<T>(
  key: string,
  defaultValue: T,
  validate?: (parsed: unknown) => parsed is T,
): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = window.localStorage.getItem(key);
      if (raw === null) return defaultValue; // fehlender Key
      const parsed: unknown = JSON.parse(raw);
      // Mit Form-Guard entscheidet ausschließlich dieser (ein Consumer, der `null`
      // bewusst zulassen will, akzeptiert es dort). Ohne Guard fällt ein geparstes
      // `null` auf den harten Default zurück — wir legen `null` nie selbst ab
      // (`undefined` ⇒ removeItem), ein gespeichertes `"null"` ist also stale/getampert.
      if (validate) return validate(parsed) ? (parsed as T) : defaultValue;
      return parsed === null ? defaultValue : (parsed as T);
    } catch {
      return defaultValue;
    }
  });

  // Aktueller Wert als Ref, damit der Setter die Updater-Form (prev => next) auflösen
  // kann, ohne den Persist-Effekt in den State-Updater zu legen (kein Doppel-Write in
  // React StrictMode, klarer Daten-/Effekt-Pfad).
  const valueRef = useRef(value);
  valueRef.current = value;

  const set = useCallback<Dispatch<SetStateAction<T>>>(
    (action) => {
      const next =
        typeof action === 'function'
          ? (action as (prev: T) => T)(valueRef.current)
          : action;
      valueRef.current = next;
      setValue(next);
      try {
        if (next === undefined) {
          window.localStorage.removeItem(key);
        } else {
          window.localStorage.setItem(key, JSON.stringify(next));
        }
      } catch {
        // Persistenz ist Komfort — die Auswahl gilt für die Session trotzdem.
      }
    },
    [key],
  );

  return [value, set];
}
